/**
 * AI Acties Pagina — KLANTZICHT op wat de AI ECHT doet.
 *
 * 2 secties:
 *   1. Live Uitvoering (execution_jobs) — eerlijke status per opdracht:
 *      ✓ uitgevoerd / ⏳ wachtrij / 🔌 koppeling nodig / 🔑 API-goedkeuring
 *      / 👤 uw actie / ❌ mislukt
 *   2. AI Voorstellen (legacy ai_actions queue) — pending approval items
 *
 * GEEN nep-badges. Élke status is echte status uit execution_engine.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import usePlan from '../../hooks/usePlan';
import {
  Sparkles, Zap, Clock, CheckCircle2, XCircle, AlertCircle,
  Headphones, MousePointerClick, BookOpen, Link2, RefreshCw,
  Loader2, Plug, KeyRound, UserCheck, ExternalLink, FileCheck, Copy,
} from 'lucide-react';
import { toast } from 'sonner';

/* ─── Honest execution status meta — kleurcodering zoals afgestemd ─── */
const JOB_STATUS = {
  completed: {
    label: 'Uitgevoerd door AI',
    color: '#15803d', bg: '#dcfce7', border: '#86efac',
    icon: CheckCircle2,
    description: 'AI heeft deze actie succesvol uitgevoerd.',
  },
  running: {
    label: 'Wordt nu uitgevoerd',
    color: '#1d4ed8', bg: '#dbeafe', border: '#93c5fd',
    icon: Loader2,
    description: 'AI voert deze actie op dit moment uit.',
    spin: true,
  },
  queued: {
    label: 'In wachtrij',
    color: '#a16207', bg: '#fef9c3', border: '#fde047',
    icon: Clock,
    description: 'Wachtrij — AI pakt dit binnen ~15 minuten op.',
  },
  awaiting_integration: {
    label: 'Wacht op koppeling',
    color: '#0369a1', bg: '#e0f2fe', border: '#7dd3fc',
    icon: Plug,
    description: 'AI kan niet uitvoeren zonder uw account-koppeling.',
  },
  pending_api_approval: {
    label: 'Wacht op API-goedkeuring',
    color: '#c2410c', bg: '#ffedd5', border: '#fdba74',
    icon: KeyRound,
    description: 'Wij wachten op API-toegang bij externe partij (Google/Meta).',
  },
  requires_customer_action: {
    label: 'Uw actie vereist',
    color: '#6d28d9', bg: '#ede9fe', border: '#c4b5fd',
    icon: UserCheck,
    description: 'AI kan pas uitvoeren nadat u iets heeft gedaan.',
  },
  team_handling: {
    label: 'Ops team plaatst',
    color: '#0e7490', bg: '#cffafe', border: '#67e8f9',
    icon: Headphones,
    description: 'Onze specialist plaatst dit handmatig in uw CMS (binnen 5 werkdagen).',
  },
  failed: {
    label: 'Mislukt',
    color: '#b91c1c', bg: '#fee2e2', border: '#fca5a5',
    icon: XCircle,
    description: 'Uitvoering is mislukt — IMPERIA AI team kijkt ernaar.',
  },
  no_executor: {
    label: 'Handmatige verwerking',
    color: '#475569', bg: '#f1f5f9', border: '#cbd5e1',
    icon: FileCheck,
    description: 'Geen geautomatiseerde executor — ops team verwerkt handmatig.',
  },
};

/* Legacy ai_actions modes/statussen (voor 2e tab) */
const MODE = {
  suggest: { label: 'Tip', icon: Sparkles, color: '#64748b', bg: '#f1f5f9' },
  guided: { label: 'Stap voor stap', icon: BookOpen, color: '#2563eb', bg: '#eff6ff' },
  one_click: { label: '1-Click', icon: MousePointerClick, color: '#059669', bg: '#ecfdf5' },
  managed: { label: 'Door IMPERIA AI', icon: Headphones, color: '#7c3aed', bg: '#f5f3ff' },
};
const STATUS_META = {
  suggested:   { label: 'Voorgesteld',   color: '#64748b' },
  approved:    { label: 'Goedgekeurd',   color: '#16a34a' },
  executing:   { label: 'Uitvoeren...',  color: '#2563eb' },
  completed:   { label: 'Voltooid',      color: '#16a34a' },
  failed:      { label: 'Mislukt',       color: '#dc2626' },
  blocked:     { label: 'Wacht op...',   color: '#D4A017' },
  rejected:    { label: 'Afgewezen',     color: '#94a3b8' },
  rolled_back: { label: 'Teruggedraaid', color: '#94a3b8' },
};

/* ─── Status pill ─── */
function StatusBadge({ status }) {
  const meta = JOB_STATUS[status] || JOB_STATUS.no_executor;
  const Icon = meta.icon;
  return (
    <span
      data-testid={`job-status-${status}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700,
        background: meta.bg, color: meta.color,
        border: `1px solid ${meta.border}`,
      }}
    >
      <Icon size={12} className={meta.spin ? 'spin' : ''} style={meta.spin ? { animation: 'spin 1s linear infinite' } : {}} />
      {meta.label}
    </span>
  );
}

/* ─── Stats banner ─── */
function StatsBanner({ stats }) {
  const items = [
    { key: 'completed',                meta: JOB_STATUS.completed },
    { key: 'running',                  meta: JOB_STATUS.running },
    { key: 'queued',                   meta: JOB_STATUS.queued },
    { key: 'awaiting_integration',     meta: JOB_STATUS.awaiting_integration },
    { key: 'pending_api_approval',     meta: JOB_STATUS.pending_api_approval },
    { key: 'requires_customer_action', meta: JOB_STATUS.requires_customer_action },
    { key: 'team_handling',            meta: JOB_STATUS.team_handling },
    { key: 'failed',                   meta: JOB_STATUS.failed },
  ];
  return (
    <div data-testid="execution-stats-banner" style={{
      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: 10, marginBottom: 16,
    }}>
      {items.map(({ key, meta }) => {
        const n = stats?.[key] || 0;
        const Icon = meta.icon;
        return (
          <div key={key} data-testid={`stat-${key}`} style={{
            background: 'white', borderRadius: 12, padding: '12px 14px',
            border: `1px solid ${meta.border}`, display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <div style={{
              width: 32, height: 32, borderRadius: 10, background: meta.bg,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Icon size={16} style={{ color: meta.color }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', lineHeight: 1, fontFamily: "'Sora',sans-serif" }}>
                {n}
              </p>
              <p style={{ fontSize: 10, color: meta.color, fontWeight: 700, marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {meta.label}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Snippet panel (alleen voor jobs met cms_mode='snippet') ─── */
function SnippetPanel({ jobId }) {
  const { api } = useAuth();
  const [snippet, setSnippet] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api('get', '/api/platform/snippets')
      .then((r) => {
        const match = (r.data.snippets || []).find((s) => s.job_id === jobId);
        if (match) setSnippet(match);
      })
      .catch(() => {});
  }, [jobId, api]);

  if (!snippet) return null;

  const textToCopy = snippet.snippet_html || snippet.snippet_text || '';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      toast.success('Gekopieerd naar klembord');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Kopiëren mislukt');
    }
  };

  const markPasted = async () => {
    try {
      await api('post', `/api/platform/snippets/${snippet.id}/mark-pasted`);
      toast.success('Gemarkeerd als geplakt — bedankt!');
    } catch {
      toast.error('Kon niet markeren');
    }
  };

  return (
    <div data-testid={`snippet-panel-${snippet.id}`} style={{
      background: '#0f172a', borderRadius: 10, padding: '12px 14px', marginTop: 4,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 6 }}>
        <p style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          📋 Kant-en-klare snippet · {snippet.time_estimate}
        </p>
        <button
          data-testid={`copy-snippet-${snippet.id}`}
          onClick={copy}
          style={{
            background: copied ? '#15803d' : '#D4A017', color: copied ? 'white' : '#0f172a',
            border: 'none', borderRadius: 6, padding: '5px 12px',
            fontSize: 11, fontWeight: 700, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}>
          <Copy size={12} /> {copied ? 'Gekopieerd!' : 'Kopieer'}
        </button>
      </div>
      <pre style={{
        background: '#020617', color: '#e2e8f0', padding: 10, borderRadius: 6,
        fontSize: 11, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        fontFamily: "'JetBrains Mono', monospace", lineHeight: 1.5, maxHeight: 200,
      }}>{textToCopy}</pre>
      <p style={{ fontSize: 11, color: '#cbd5e1', marginTop: 8, lineHeight: 1.5 }}>
        <b style={{ color: '#fbbf24' }}>Waar plakken:</b> {snippet.where_to_paste}
      </p>
      <button
        data-testid={`mark-pasted-${snippet.id}`}
        onClick={markPasted}
        style={{
          marginTop: 8, background: 'transparent', color: '#94a3b8',
          border: '1px solid #334155', borderRadius: 6, padding: '4px 10px',
          fontSize: 10, fontWeight: 600, cursor: 'pointer',
        }}>
        ✓ Ik heb dit geplakt
      </button>
    </div>
  );
}

/* ─── Job card (execution_jobs) ─── */
function JobCard({ job }) {
  const meta = JOB_STATUS[job.status] || JOB_STATUS.no_executor;
  const reason = job.reason || meta.description;
  const hasBefore = job.huidige_situatie;
  const hasAfter = job.beter_voorstel;

  return (
    <div data-testid={`execution-job-${job.id}`} style={{
      background: 'white', borderRadius: 12, border: '1px solid #e2e8f0',
      padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: 3 }}>
            {job.category || 'algemeen'}{job.executor ? ` · ${job.executor}` : ''}
          </p>
          <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: 14, fontWeight: 800, color: '#0f172a' }}>
            {job.title || 'Onbekende actie'}
          </h3>
        </div>
        <StatusBadge status={job.status} />
      </div>

      {/* Honest reason text */}
      {reason && (
        <p data-testid="job-reason" style={{
          fontSize: 12, color: '#475569', lineHeight: 1.55,
          background: meta.bg, padding: '8px 10px', borderRadius: 8,
          border: `1px solid ${meta.border}`,
        }}>
          {reason}
        </p>
      )}

      {/* Huidig → Beter snippet */}
      {(hasBefore || hasAfter) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {hasBefore && (
            <div style={{ background: '#fef2f2', borderLeft: '3px solid #f87171', borderRadius: '0 6px 6px 0', padding: '8px 10px' }}>
              <p style={{ fontSize: 9, color: '#b91c1c', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>Huidig</p>
              <p style={{ fontSize: 11, color: '#0f172a', fontStyle: 'italic', lineHeight: 1.4, marginTop: 2 }}>
                &ldquo;{(job.huidige_situatie || '').slice(0, 200)}{job.huidige_situatie?.length > 200 ? '…' : ''}&rdquo;
              </p>
            </div>
          )}
          {hasAfter && (
            <div style={{ background: '#fff8e1', borderLeft: '3px solid #D4A017', borderRadius: '0 6px 6px 0', padding: '8px 10px' }}>
              <p style={{ fontSize: 9, color: '#D4A017', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>Beter voorstel</p>
              <p style={{ fontSize: 11, color: '#0f172a', fontWeight: 600, lineHeight: 1.4, marginTop: 2 }}>
                {(job.beter_voorstel || '').slice(0, 200)}{job.beter_voorstel?.length > 200 ? '…' : ''}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Evidence (completed) of customer action link */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {job.status === 'completed' && job.evidence_url && !(job.cms_mode === 'snippet') && (
          <a
            data-testid={`job-evidence-${job.id}`}
            href={job.evidence_url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              background: '#15803d', color: 'white', padding: '6px 12px',
              borderRadius: 8, fontSize: 11, fontWeight: 700, textDecoration: 'none',
            }}
          >
            <ExternalLink size={12} /> Bekijk live resultaat
          </a>
        )}
        {job.evidence_note && (
          <span style={{ fontSize: 11, color: '#64748b', fontStyle: 'italic' }}>
            {job.evidence_note}
          </span>
        )}
        {job.customer_action_link && (
          <a
            data-testid={`job-action-${job.id}`}
            href={job.customer_action_link}
            target={job.customer_action_link.startsWith('http') ? '_blank' : '_self'}
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              background: meta.color, color: 'white', padding: '6px 12px',
              borderRadius: 8, fontSize: 11, fontWeight: 700, textDecoration: 'none',
            }}
          >
            {job.customer_action_label || 'Open actie'} <ExternalLink size={11} />
          </a>
        )}
        {job.last_error && job.status === 'failed' && (
          <span style={{ fontSize: 10, color: '#b91c1c', fontStyle: 'italic', maxWidth: '100%' }}>
            {job.last_error.slice(0, 200)}
          </span>
        )}
      </div>

      {/* Copy-paste snippet panel — alleen voor Wix/Squarespace/Webador/JouwWeb klanten */}
      {job.cms_mode === 'snippet' && job.status === 'completed' && (
        <SnippetPanel jobId={job.id} />
      )}
    </div>
  );
}

/* ─── BlockerHint (legacy ai_actions) ─── */
function BlockerHint({ blocker }) {
  if (blocker.startsWith('integration_required:')) {
    const cat = blocker.split(':')[1];
    return <span>🔗 Koppel uw {cat}-account in <Link to="/platform/integraties" style={{ color: '#D4A017', fontWeight: 700 }}>Integraties</Link></span>;
  }
  if (blocker === 'upgrade_required') return <span>💎 Upgrade naar €14,95 excl. btw voor uitvoer</span>;
  if (blocker === 'plan_upgrade_required:beheer_paid') return <span>👑 Vereist Managed AI Bot (vanaf €995,95 excl. btw eerste maand)</span>;
  if (blocker === 'approval_required') return <span>⏳ Wacht op goedkeuring</span>;
  return <span>{blocker}</span>;
}

/* ─── Action card (legacy ai_actions) ─── */
function ActionCard({ action, onApprove, onExecute, onReject, busy }) {
  const m = MODE[action.execution_mode] || MODE.suggest;
  const s = STATUS_META[action.status] || {};
  const Icon = m.icon;
  const isPending = ['suggested', 'approved'].includes(action.status);
  const canApprove = action.status === 'suggested' && action.execution_mode !== 'managed';
  const canExecute = action.status === 'approved' || (action.status === 'suggested' && action.execution_mode === 'one_click');

  return (
    <div data-testid={`ai-action-${action.id}`} style={{
      background: 'white', borderRadius: 12, border: '1px solid #e2e8f0',
      padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: 3 }}>
            {action.category}
          </p>
          <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: 14, fontWeight: 800, color: '#0f172a' }}>{action.title}</h3>
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '3px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700, background: m.bg, color: m.color, border: `1px solid ${m.color}30` }}>
            <Icon size={11} /> {m.label}
          </span>
          <span style={{ padding: '3px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700, background: `${s.color}15`, color: s.color }}>
            {s.label}
          </span>
        </div>
      </div>

      {action.description && <p style={{ fontSize: 12, color: '#64748b', lineHeight: 1.55 }}>{action.description}</p>}

      {action.payload?.before && action.payload?.after && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ background: '#fef2f2', borderLeft: '3px solid #f87171', borderRadius: '0 6px 6px 0', padding: '8px 10px' }}>
            <p style={{ fontSize: 9, color: '#b91c1c', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>Huidig</p>
            <p style={{ fontSize: 11, color: '#0f172a', fontStyle: 'italic', lineHeight: 1.4, marginTop: 2 }}>&ldquo;{action.payload.before}&rdquo;</p>
          </div>
          <div style={{ background: '#fff8e1', borderLeft: '3px solid #D4A017', borderRadius: '0 6px 6px 0', padding: '8px 10px' }}>
            <p style={{ fontSize: 9, color: '#D4A017', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>Beter voorstel</p>
            <p style={{ fontSize: 11, color: '#0f172a', fontWeight: 600, lineHeight: 1.4, marginTop: 2 }}>{action.payload.after}</p>
          </div>
        </div>
      )}

      {action.blockers && action.blockers.length > 0 && (
        <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: '8px 10px', fontSize: 11, color: '#9a3412' }}>
          <p style={{ fontWeight: 700, marginBottom: 2 }}>Wacht op:</p>
          {action.blockers.map((b, i) => <p key={i} style={{ marginTop: 2 }}><BlockerHint blocker={b} /></p>)}
        </div>
      )}

      {isPending && (
        <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
          {canApprove && (
            <button data-testid={`approve-action-${action.id}`} onClick={() => onApprove(action.id)} disabled={busy}
              style={{ background: '#16a34a', color: 'white', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
              ✓ Goedkeuren
            </button>
          )}
          {canExecute && (
            <button data-testid={`execute-action-${action.id}`} onClick={() => onExecute(action.id)} disabled={busy}
              style={{ background: '#D4A017', color: '#0f172a', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
              ⚡ Uitvoeren
            </button>
          )}
          <button onClick={() => onReject(action.id)} disabled={busy}
            style={{ background: 'white', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: 8, padding: '7px 14px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
            Afwijzen
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── Main page ─── */
export default function AiActiesPage() {
  const { api } = useAuth();
  const plan = usePlan();
  const isManaged = plan.isManaged;

  // Tab: 'live' (execution_jobs) of 'voorstellen' (ai_actions)
  const [tab, setTab] = useState(isManaged ? 'live' : 'voorstellen');

  // Live execution jobs
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState({});
  const [jobFilter, setJobFilter] = useState('all');
  const [jobsLoading, setJobsLoading] = useState(true);

  // Legacy AI actions
  const [actions, setActions] = useState([]);
  const [actionsLoading, setActionsLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [actionFilter, setActionFilter] = useState('all');

  /* Fetchers — gebruiken een synchroon ref-flag voor cancel-safety */
  const loadJobs = useCallback(async () => {
    try {
      const r = await api('get', '/api/platform/execution/jobs?limit=200');
      setJobs(r.data.jobs || []);
      setStats(r.data.stats || {});
    } catch (e) {
      console.error('jobs load failed', e);
    } finally {
      setJobsLoading(false);
    }
  }, [api]);

  const loadActions = useCallback(async () => {
    try {
      const url = actionFilter === 'all' ? '/api/ai/actions' : `/api/ai/actions?status=${actionFilter}`;
      const r = await api('get', url);
      setActions(r.data.actions || []);
    } catch { /* ignore */ }
    finally {
      setActionsLoading(false);
    }
  }, [api, actionFilter]);

  useEffect(() => {
    if (tab === 'live') {
      api('get', '/api/platform/execution/jobs?limit=200')
        .then((r) => { setJobs(r.data.jobs || []); setStats(r.data.stats || {}); })
        .catch((e) => console.error('jobs load failed', e))
        .finally(() => setJobsLoading(false));
    } else if (tab === 'voorstellen') {
      const url = actionFilter === 'all' ? '/api/ai/actions' : `/api/ai/actions?status=${actionFilter}`;
      api('get', url)
        .then((r) => setActions(r.data.actions || []))
        .catch(() => {})
        .finally(() => setActionsLoading(false));
    }
  }, [tab, actionFilter, api]);

  /* AI Actions handlers */
  const approve = async (id) => {
    setBusy(true);
    try { await api('post', `/api/ai/actions/${id}/approve`); toast.success('Goedgekeurd'); await loadActions(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Mislukt'); }
    setBusy(false);
  };
  const execute = async (id) => {
    setBusy(true);
    try {
      const r = await api('post', `/api/ai/actions/${id}/execute`);
      if (r.data.result?.executed) toast.success('Actie uitgevoerd');
      else if (r.data.result?.status === 'pending_integration') toast.info('Wacht op CMS koppeling — handmatige stappen worden getoond');
      else toast.error('Uitvoering geblokkeerd');
      await loadActions();
    } catch (e) { toast.error(e.response?.data?.detail || 'Mislukt'); }
    setBusy(false);
  };
  const reject = async (id) => {
    setBusy(true);
    try { await api('post', `/api/ai/actions/${id}/reject`); toast.info('Afgewezen'); await loadActions(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Mislukt'); }
    setBusy(false);
  };

  /* Filtered jobs */
  const filteredJobs = jobFilter === 'all' ? jobs : jobs.filter(j => j.status === jobFilter);

  const JOB_FILTERS = [
    { v: 'all', l: 'Alles' },
    { v: 'completed', l: 'Uitgevoerd' },
    { v: 'running', l: 'Bezig' },
    { v: 'queued', l: 'Wachtrij' },
    { v: 'awaiting_integration', l: 'Wacht op koppeling' },
    { v: 'pending_api_approval', l: 'Wacht op API' },
    { v: 'requires_customer_action', l: 'Uw actie nodig' },
    { v: 'team_handling', l: 'Ops team plaatst' },
    { v: 'failed', l: 'Mislukt' },
  ];

  return (
    <div data-testid="ai-acties-page">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
        <div>
          <h1 style={{ fontFamily: "'Sora',sans-serif", fontSize: 22, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>
            <Zap size={18} style={{ display: 'inline', color: '#D4A017', marginRight: 8, verticalAlign: 'middle' }} />
            AI Acties — eerlijk inzicht in wat onze AI doet
          </h1>
          <p style={{ color: '#64748b', fontSize: 13 }}>
            Géén nep-badges. U ziet exact wat is uitgevoerd, wat klaarstaat en wat AI tegenhoudt.
          </p>
        </div>
        <button
          data-testid="refresh-btn"
          onClick={tab === 'live' ? loadJobs : loadActions}
          style={{
            background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '8px 14px',
            fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
          }}>
          <RefreshCw size={13} /> Vernieuwen
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, borderBottom: '1px solid #e2e8f0' }}>
        {isManaged && (
          <button
            data-testid="tab-live"
            onClick={() => setTab('live')}
            style={{
              background: 'transparent', border: 'none',
              borderBottom: tab === 'live' ? '2px solid #0f172a' : '2px solid transparent',
              color: tab === 'live' ? '#0f172a' : '#94a3b8',
              fontWeight: 700, fontSize: 13, padding: '10px 14px', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
            <Zap size={14} /> Live Uitvoering
            <span style={{ background: '#fef3c7', color: '#92400e', padding: '1px 6px', borderRadius: 999, fontSize: 9, fontWeight: 700 }}>
              {jobs.length}
            </span>
          </button>
        )}
        <button
          data-testid="tab-voorstellen"
          onClick={() => setTab('voorstellen')}
          style={{
            background: 'transparent', border: 'none',
            borderBottom: tab === 'voorstellen' ? '2px solid #0f172a' : '2px solid transparent',
            color: tab === 'voorstellen' ? '#0f172a' : '#94a3b8',
            fontWeight: 700, fontSize: 13, padding: '10px 14px', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
          <Sparkles size={14} /> AI Voorstellen
        </button>
      </div>

      {/* TAB: live execution_jobs */}
      {tab === 'live' && (
        <div data-testid="tab-content-live">
          {isManaged ? (
            <>
              <StatsBanner stats={stats} />

              {/* Filter pills */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
                {JOB_FILTERS.map(f => (
                  <button
                    key={f.v}
                    data-testid={`job-filter-${f.v}`}
                    onClick={() => setJobFilter(f.v)}
                    style={{
                      background: jobFilter === f.v ? '#0f172a' : 'white',
                      color: jobFilter === f.v ? 'white' : '#64748b',
                      border: '1px solid ' + (jobFilter === f.v ? '#0f172a' : '#e2e8f0'),
                      borderRadius: 999, padding: '5px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                    }}>
                    {f.l}{f.v !== 'all' && stats[f.v] != null ? ` (${stats[f.v]})` : ''}
                  </button>
                ))}
              </div>

              {jobsLoading ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Laden…</div>
              ) : filteredJobs.length === 0 ? (
                <div data-testid="no-jobs" style={{ background: 'white', borderRadius: 12, border: '1px solid #f1f5f9', padding: 40, textAlign: 'center' }}>
                  <Zap size={28} style={{ color: '#D4A017', marginBottom: 12 }} />
                  <p style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Geen uitvoer-jobs in deze status</p>
                  <p style={{ fontSize: 12, color: '#64748b' }}>Probeer een andere filter of vernieuw de pagina.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {filteredJobs.map(j => <JobCard key={j.id} job={j} />)}
                </div>
              )}
            </>
          ) : (
            <div data-testid="live-managed-only" style={{
              background: 'linear-gradient(135deg, #7c3aed, #5b21b6)',
              borderRadius: 16, padding: 28, color: 'white', textAlign: 'center',
            }}>
              <Headphones size={36} style={{ marginBottom: 12, opacity: 0.9 }} />
              <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: 18, fontWeight: 800, marginBottom: 6 }}>
                Live AI uitvoering is onderdeel van Managed AI
              </h3>
              <p style={{ fontSize: 13, opacity: 0.85, marginBottom: 14, maxWidth: 480, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.5 }}>
                Hier ziet u onze AI uw site direct aanpassen (WordPress, Shopify, GBP, reviews) en welke
                acties wij voor u uitvoeren of waar we op wachten.
              </p>
              <Link to="/platform/checkout?plan=imperium_beheer" style={{
                display: 'inline-block', background: '#D4A017', color: '#0f172a',
                padding: '10px 20px', borderRadius: 10, textDecoration: 'none',
                fontWeight: 700, fontSize: 13,
              }}>
                Activeer Managed AI · €995,95 excl. btw eerste maand
              </Link>
            </div>
          )}
        </div>
      )}

      {/* TAB: legacy ai_actions */}
      {tab === 'voorstellen' && (
        <div data-testid="tab-content-voorstellen">
          <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
            {[
              { v: 'all', l: 'Alles' },
              { v: 'suggested', l: 'Voorgesteld' },
              { v: 'approved', l: 'Goedgekeurd' },
              { v: 'executing', l: 'Uitvoeren' },
              { v: 'completed', l: 'Voltooid' },
              { v: 'blocked', l: 'Wacht op...' },
            ].map(f => (
              <button
                key={f.v}
                data-testid={`filter-${f.v}`}
                onClick={() => setActionFilter(f.v)}
                style={{
                  background: actionFilter === f.v ? '#0f172a' : 'white',
                  color: actionFilter === f.v ? 'white' : '#64748b',
                  border: '1px solid ' + (actionFilter === f.v ? '#0f172a' : '#e2e8f0'),
                  borderRadius: 6, padding: '6px 14px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                }}>
                {f.l}
              </button>
            ))}
          </div>

          {actionsLoading ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Laden…</div>
          ) : actions.length === 0 ? (
            <div style={{ background: 'white', borderRadius: 12, border: '1px solid #f1f5f9', padding: 40, textAlign: 'center' }}>
              <Sparkles size={28} style={{ color: '#D4A017', marginBottom: 12 }} />
              <p style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Nog geen voorstellen</p>
              <p style={{ fontSize: 12, color: '#64748b' }}>Bekijk uw verbeterpunten om AI-voorstellen te genereren.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {actions.map(a => (
                <ActionCard key={a.id} action={a} onApprove={approve} onExecute={execute} onReject={reject} busy={busy} />
              ))}
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
